import { useCallback } from "react";
import { Controller, FieldPath, useFormContext, useWatch } from "react-hook-form";
import { styled } from "styled-components";
import { Input } from "../../components/Input";
import { Checkbox } from "../../components/Checkbox";
import { Row, Col } from "../../components/Flex";

const StepHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
`;

const StepEyebrow = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
`;

const StepTitle = styled.h2`
  margin: 0;
  font-family: "Sofia Pro", sans-serif;
  font-size: 30px;
  line-height: 1.1;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue900};

  @media (max-width: 767px) {
    font-size: 24px;
  }
`;

const StepText = styled.p`
  margin: 0;
  max-width: 720px;
  font-family: "Sofia Pro", sans-serif;
  font-size: 15px;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.blue500};
`;

const SectionCard = styled.section`
  padding: 22px;
  border-radius: 24px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background:
    linear-gradient(180deg, rgba(246, 163, 28, 0.06) 0%, rgba(246, 163, 28, 0) 100%),
    ${({ theme }) => theme.colors.white};

  & + & {
    margin-top: 18px;
  }

  @media (max-width: 767px) {
    padding: 18px;
    border-radius: 20px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-family: "Sofia Pro", sans-serif;
  font-size: 22px;
  line-height: 1.2;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue900};
`;

const SectionText = styled.p`
  margin: 0;
  font-family: "Sofia Pro", sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.blue500};
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-family: "Sofia Pro", sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue900};
`;

const FormItem = styled.div`
  margin-bottom: 22px;
`;

const ToggleCard = styled.div`
  margin: 18px 0;
  padding: 16px 18px;
  border-radius: 18px;
  background: ${({ theme }) => theme.colors.grey100};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const ToggleTitle = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue900};
  margin-bottom: 6px;
`;

const ToggleText = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.blue500};
  margin-bottom: 12px;
`;

export interface CheckoutFormData {
  name: string;
  surname: string;
  email: string;
  phone: string;
  street: string;
  aptNumber?: string;
  city: string;
  zip: string;
  billingName?: string;
  billingSurname?: string;
  billingStreet?: string;
  billingAptNumber?: string;
  billingCity?: string;
  billingZip?: string;
  sameAddress: boolean;
}

interface FieldConfig {
  name: FieldPath<CheckoutFormData>;
  label: string;
  type?: string;
  span?: number;
  sm?: number;
  md?: number;
  required?: string;
  pattern?: {
    value: RegExp;
    message: string;
  };
}

const deliveryFields: FieldConfig[] = [
  { name: "name", label: "Jméno", sm: 12, required: "Jméno je povinné" },
  { name: "surname", label: "Příjmení", sm: 12, required: "Příjmení je povinné" },
  {
    name: "email",
    label: "Email",
    type: "email",
    sm: 12,
    required: "Email je povinný",
    pattern: {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Neplatný formát emailu",
    },
  },
  {
    name: "phone",
    label: "Telefon",
    type: "tel",
    sm: 12,
    required: "Telefon je povinný",
    pattern: {
      value: /^[+]?[\d\s-]{9,}$/,
      message: "Neplatný formát telefonu",
    },
  },
  { name: "street", label: "Ulice", sm: 16, required: "Ulice je povinná" },
  { name: "aptNumber", label: "Číslo bytu", sm: 8 },
  { name: "city", label: "Město", sm: 12, md: 8, required: "Město je povinné" },
  {
    name: "zip",
    label: "PSČ",
    sm: 12,
    md: 8,
    required: "PSČ je povinné",
    pattern: {
      value: /^\d{5}$/,
      message: "Formát PSČ není správný (př.: 62500)",
    },
  },
];

const billingFields: FieldConfig[] = [
  { name: "billingName", label: "Jméno", sm: 12, required: "Jméno je povinné" },
  { name: "billingSurname", label: "Příjmení", sm: 12, required: "Příjmení je povinné" },
  { name: "billingStreet", label: "Ulice", sm: 16, required: "Ulice je povinná" },
  { name: "billingAptNumber", label: "Číslo bytu", sm: 8 },
  { name: "billingCity", label: "Město", sm: 12, md: 8, required: "Město je povinné" },
  {
    name: "billingZip",
    label: "PSČ",
    sm: 12,
    md: 8,
    required: "PSČ je povinné",
    pattern: {
      value: /^\d{5}$/,
      message: "Formát PSČ není správný (př.: 62500)",
    },
  },
];

const FieldRenderer = ({ fields }: { fields: FieldConfig[] }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<CheckoutFormData>();

  return (
    <Row gutter={24}>
      {fields.map((field) => (
        <Col key={field.name} span={24} sm={field.sm} md={field.md}>
          <FormItem>
            <FormLabel htmlFor={field.name}>{field.label}</FormLabel>
            <Controller
              name={field.name}
              control={control}
              rules={{
                required: field.required,
                pattern: field.pattern,
              }}
              render={({ field: controllerField }) => {
                const { value, ...restField } = controllerField;

                return (
                  <Input
                    id={field.name}
                    type={field.type}
                    {...restField}
                    value={typeof value === "string" ? value : ""}
                    error={errors[field.name]?.message as string | undefined}
                  />
                );
              }}
            />
          </FormItem>
        </Col>
      ))}
    </Row>
  );
};

const Second = () => {
  const { control, setValue } = useFormContext<CheckoutFormData>();
  const isSameAddress = useWatch({
    control,
    name: "sameAddress",
  });

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue("sameAddress", event.target.checked, {
        shouldDirty: true,
      });
    },
    [setValue]
  );

  return (
    <>
      <StepHeader>
        <StepEyebrow>Krok 2</StepEyebrow>
        <StepTitle>Kam máme objednávku potvrdit?</StepTitle>
        <StepText>
          Kontaktní údaje používáme pouze pro potvrzení objednávky a případnou komunikaci k platbě.
          Fakturační část se zobrazí jen tehdy, když se liší od dodací adresy.
        </StepText>
      </StepHeader>

      <SectionCard>
        <SectionHeader>
          <SectionTitle>Dodací údaje</SectionTitle>
          <SectionText>
            Na tento e-mail pošleme potvrzení objednávky. Telefon použijeme pouze v případě potřeby.
          </SectionText>
        </SectionHeader>
        <FieldRenderer fields={deliveryFields} />
      </SectionCard>

      <ToggleCard>
        <ToggleTitle>Fakturační údaje</ToggleTitle>
        <ToggleText>
          Pokud je fakturační adresa stejná, nebudeme vás nutit vyplňovat stejná data dvakrát.
        </ToggleText>
        <Checkbox
          checked={isSameAddress}
          onChange={handleCheckboxChange}
        >
          Fakturační adresa je stejná jako dodací
        </Checkbox>
      </ToggleCard>

      {!isSameAddress && (
        <SectionCard>
          <SectionHeader>
            <SectionTitle>Fakturační údaje</SectionTitle>
            <SectionText>
              Tuto sekci vyplňte jen v případě, že chcete použít jinou fakturační adresu.
            </SectionText>
          </SectionHeader>
          <FieldRenderer fields={billingFields} />
        </SectionCard>
      )}
    </>
  );
};

export default Second;
